import React, { useContext, useEffect, useMemo, useState } from "react";
import { Coins } from "lucide-react";
import { api } from "@/lib/api";
import { waLink, WhatsAppIcon } from "@/components/WhatsAppButton";
import { useMemberMessage, fieldKey, MemberEnquiryContext } from "@/lib/waTemplates";
import { normalizeQuick, fieldOptions, estimatePoints, ruleText } from "@/lib/quickEnquiry";
import { SearchSelect, TravellersPicker, travellersText } from "@/components/quick/Pickers";

const pad = (n) => String(n).padStart(2, "0");
const today = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const clean = (v) => String(v ?? "").replace(/[<>*_~`]/g, "").trim().slice(0, 300);
const cleanContact = (v) => String(v ?? "").replace(/[<>`]/g, "").trim().slice(0, 300);
const inputCls = "w-full h-11 rounded-lg px-3 text-sm bg-transparent";
const CONTACT = [
  { k: "name", label: "Name", type: "text", required: true, auto: "name" },
  { k: "mobile", label: "Mobile Number", type: "tel", required: true, auto: "tel" },
  { k: "email", label: "Email", type: "email", required: false, auto: "email" },
];

function FieldInput({ f, value, onChange, lists, minDate, error, testid }) {
  const border = { border: `1px solid ${error ? "#dc2626" : "var(--border-strong)"}` };
  if (f.type === "search") return <SearchSelect value={value} onChange={onChange} options={fieldOptions(f, lists)} placeholder={f.placeholder} error={error} testid={testid} />;
  if (f.type === "travellers") return <TravellersPicker value={value} onChange={onChange} testid={testid} />;
  if (f.type === "select" || f.type === "triptype") {
    const opts = f.type === "triptype" ? ["One-way", "Return"] : fieldOptions(f, lists);
    return (
      <select value={value || (f.type === "triptype" ? "One-way" : "")} onChange={(e) => onChange(e.target.value)} className={inputCls} style={border} data-testid={testid}>
        {f.type !== "triptype" && <option value="">{f.placeholder || "Select"}</option>}
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (f.type === "date") return <input type="date" min={minDate} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} style={border} data-testid={testid} />;
  if (f.type === "textarea") return <textarea rows={1} maxLength={300} value={value || ""} placeholder={f.placeholder} onChange={(e) => onChange(e.target.value)} className={`${inputCls} py-2.5 resize-none`} style={border} data-testid={testid} />;
  return <input type="text" maxLength={80} value={value || ""} placeholder={f.placeholder} onChange={(e) => onChange(e.target.value)} className={inputCls} style={border} data-testid={testid} />;
}

export default function QuickEnquiry({ c: raw, wa, wrap, H2, Ico }) {
  const c = useMemo(() => normalizeQuick(raw), [raw]);
  const isMember = !!useContext(MemberEnquiryContext);
  const memberMsg = useMemberMessage("quick");
  const [tab, setTab] = useState(0);
  const [vals, setVals] = useState({});
  const [errors, setErrors] = useState({});
  const [rules, setRules] = useState(null);
  useEffect(() => {
    if (isMember) api.get("/customer/earn-rules").then(({ data }) => setRules(data)).catch(() => {});
  }, [isMember]);
  const t = c.tabs[tab] || c.tabs[0];
  if (!t) return null;
  const budget = isMember ? c.budgets.find((b) => b.label === vals[`b-${tab}`]) : null;
  const est = estimatePoints(rules, t.service, budget);
  const k = (i) => `${tab}-${i}`;
  const isReturn = (vals[k(t.fields.findIndex((f) => f.type === "triptype"))] || "One-way") === "Return";
  const visible = t.fields.map((f, i) => ({ f, i })).filter(({ f }) => !f.returnOnly || isReturn);
  const shown = (f, i) => {
    const v = vals[k(i)];
    if (f.type === "travellers") return travellersText(v || { a: 1, c: 0, i: 0 });
    if (f.type === "triptype") return v || "One-way";
    return clean(v);
  };
  const contact = c.contact?.enabled ? (isMember ? [] : CONTACT) : [];
  const notesOn = c.contact?.enabled;
  const setVal = (key, v) => {
    const next = { ...vals, [key]: v };
    // Clear any later date in this tab that is now before the newly picked date
    const di = t.fields.findIndex((_, i) => k(i) === key);
    if (t.fields[di]?.type === "date" && v) {
      t.fields.forEach((f, i) => { if (i > di && f.type === "date" && next[k(i)] && next[k(i)] < v) next[k(i)] = ""; });
    }
    setVals(next); setErrors({ ...errors, [key]: false });
  };

  const lines = visible.map(({ f, i }) => (shown(f, i) ? `${f.label}: ${shown(f, i)}` : null)).filter(Boolean);
  const extra = [budget ? `Budget: ${budget.label}` : null,
                 ...contact.map((x) => (cleanContact(vals[`c-${x.k}`]) ? `${x.label}: ${cleanContact(vals[`c-${x.k}`])}` : null)),
                 notesOn && cleanContact(vals["c-notes"]) ? `Notes: ${cleanContact(vals["c-notes"])}` : null].filter(Boolean);
  const details = [...lines, ...extra].join("\n");
  const msg = memberMsg
    ? memberMsg({ enquiry_type: t.label, details, ...Object.fromEntries(visible.map(({ f, i }) => [fieldKey(f.label), shown(f, i)])) })
    : `Hi Zola, I'd like to enquire about ${t.label}:\n${details}`;

  const validate = (e) => {
    const missing = {};
    visible.forEach(({ f, i }) => { if (f.required && !shown(f, i)) missing[k(i)] = true; });
    contact.forEach((x) => { if (x.required && !cleanContact(vals[`c-${x.k}`])) missing[`c-${x.k}`] = true; });
    const email = cleanContact(vals["c-email"]);
    if (email && !/^\S+@\S+\.\S+$/.test(email)) missing["c-email"] = true;
    const mob = cleanContact(vals["c-mobile"]);
    if (mob && !/^\+?[\d\s-]{7,16}$/.test(mob)) missing["c-mobile"] = true;
    if (Object.keys(missing).length) { e.preventDefault(); setErrors(missing); }
  };
  const applyRoute = (r) => {
    const ti = c.tabs.findIndex((x) => x.key === r.tab);
    if (ti < 0) return;
    const next = { ...vals };
    c.tabs[ti].fields.forEach((f, i) => { const fk = fieldKey(f.label); if (fk === "from" && r.from) next[`${ti}-${i}`] = r.from; if (fk === "to" && r.to) next[`${ti}-${i}`] = r.to; });
    setVals(next); setTab(ti); setErrors({});
  };
  const routes = (c.routes || []).filter((r) => r.label && c.tabs.some((x) => x.key === r.tab));
  let lastDate = today();

  return (
    <section className={`${wrap} -mt-0 pt-8`} data-testid="section-quick">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <H2 sub={c.subtitle}>{c.title}</H2>
        <div className="flex gap-2 overflow-x-auto">
          {c.tabs.map((x, i) => (
            <button key={i} type="button" onClick={() => { setTab(i); setErrors({}); }} data-testid={`quick-tab-${i}`}
                    className={`h-11 px-4 sm:px-6 rounded-lg inline-flex items-center gap-2 text-sm font-medium whitespace-nowrap transition-colors ${i === tab ? "bg-navy text-white" : ""}`}
                    style={i === tab ? {} : { background: "var(--surface)", border: "1px solid var(--border)" }}>
              <Ico name={x.icon} className="h-4 w-4" />{x.label}
            </button>
          ))}
        </div>
      </div>
      {routes.length > 0 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="quick-routes">
          <span className="text-xs self-center whitespace-nowrap" style={{ color: "var(--text-muted)" }}>Popular:</span>
          {routes.map((r, i) => (
            <button key={i} type="button" onClick={() => applyRoute(r)} data-testid={`quick-route-${i}`}
                    className="h-8 px-3 rounded-full text-xs font-medium whitespace-nowrap transition-colors hover:bg-[color:var(--bg-soft)]"
                    style={{ border: "1px solid var(--border)" }}>{r.label}</button>
          ))}
        </div>
      )}
      <div className="mt-4 rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end"
           style={{ background: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm, 0 8px 24px -16px rgba(0,0,0,.2))" }}>
        {visible.map(({ f, i }) => {
          const min = lastDate;
          if (f.type === "date" && vals[k(i)]) lastDate = vals[k(i)];
          return (
            <div key={k(i)} className="block">
              <span className="text-xs" style={{ color: errors[k(i)] ? "#dc2626" : "var(--text-muted)" }}>{f.label}{f.required ? " *" : ""}</span>
              <FieldInput f={f} value={vals[k(i)]} onChange={(v) => setVal(k(i), v)} lists={c.lists} minDate={min} error={errors[k(i)]} testid={`quick-field-${i}`} />
            </div>
          );
        })}
        {contact.map((x) => (
          <label key={x.k} className="block">
            <span className="text-xs" style={{ color: errors[`c-${x.k}`] ? "#dc2626" : "var(--text-muted)" }}>{x.label}{x.required ? " *" : ""}</span>
            <input type={x.type} autoComplete={x.auto} maxLength={80} value={vals[`c-${x.k}`] || ""} onChange={(e) => setVal(`c-${x.k}`, e.target.value)}
                   className={inputCls} style={{ border: `1px solid ${errors[`c-${x.k}`] ? "#dc2626" : "var(--border-strong)"}` }} data-testid={`quick-contact-${x.k}`} />
          </label>
        ))}
        {isMember && c.budgets.length > 0 && (
          <label className="block">
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>Trip Budget</span>
            <select value={vals[`b-${tab}`] || ""} onChange={(e) => setVal(`b-${tab}`, e.target.value)} className={inputCls}
                    style={{ border: "1px solid var(--border-strong)" }} data-testid="quick-budget">
              <option value="">Select a budget</option>
              {c.budgets.map((b) => <option key={b.label} value={b.label}>{b.label}</option>)}
            </select>
          </label>
        )}
        {notesOn && (
          <label className="block sm:col-span-2">
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>Additional Notes</span>
            <input type="text" maxLength={300} value={vals["c-notes"] || ""} onChange={(e) => setVal("c-notes", e.target.value)}
                   placeholder="Anything else we should know?" className={inputCls} style={{ border: "1px solid var(--border-strong)" }} data-testid="quick-contact-notes" />
          </label>
        )}
        {wa ? (
          <a href={waLink(wa, msg)} onClick={validate} target="_blank" rel="noopener noreferrer" data-testid="quick-whatsapp-btn"
             className="h-11 px-5 rounded-lg inline-flex items-center justify-center gap-2 text-sm font-semibold text-white lg:col-start-4" style={{ background: "#25D366" }}>
            <WhatsAppIcon className="h-5 w-5" /> {c.button}
          </a>
        ) : <span className="text-sm text-red-700" data-testid="quick-wa-unavailable">WhatsApp enquiries unavailable</span>}
        {isMember && rules && (
          <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-end gap-2 text-xs" data-testid="quick-points-preview">
            <Coins className="h-4 w-4 flex-shrink-0" style={{ color: "var(--gold)" }} />
            {est && !est.zero ? (
              <span>Earn about <b data-testid="quick-points-estimate">{est.text} pts</b>
                <span style={{ color: "var(--text-muted)" }}> (≈ AED {est.aed}) on this {t.label.toLowerCase()} booking · final points depend on your invoice</span></span>
            ) : est?.zero ? (
              <span style={{ color: "var(--text-muted)" }}>This budget is below the minimum invoice for earning points.</span>
            ) : (
              <span style={{ color: "var(--text-muted)" }}>{ruleText(rules, t.service) ? `You earn ${ruleText(rules, t.service)} on ${t.label}. ` : ""}Pick a trip budget to see your estimate.</span>
            )}
          </div>
        )}
        {Object.values(errors).some(Boolean) && (
          <p className="text-xs text-red-600 sm:col-span-2 lg:col-span-4" role="alert" data-testid="quick-errors">Please fill in the highlighted fields.</p>
        )}
      </div>
    </section>
  );
}
